/**
 * Guided question sequence for Costs & Margin.
 * Builds N Subcontract Amount questions (one per sub).
 * User-facing copy must say Subcontract Amount — never the old C-word.
 */

export type QuestionKind =
  | 'contract'
  | 'subcontract_amount'
  | 'sub_change_orders'
  | 'labor'
  | 'overhead'
  | 'other_costs'

export type SubForQuestions = {
  projectVendorId: number
  displayName: string
}

export type GuidedQuestion = {
  id: string
  kind: QuestionKind
  phaseLabel: string
  title: string
  body: string
  projectVendorId?: number
  vendorName?: string
  /** Repeatable steps must not read as a terminal "you're done" step. */
  repeatable?: boolean
}

export function subDisplayName(sub: {
  company_name?: string | null
  name?: string | null
  trade?: string | null
}): string {
  return (
    (sub.company_name && String(sub.company_name).trim())
    || (sub.name && String(sub.name).trim())
    || (sub.trade && String(sub.trade).trim())
    || 'Sub'
  )
}

/**
 * Fixed scaffold + one Subcontract Amount question per sub on the job.
 * Order: N subs → contract → sub COs → labor → overhead → other costs.
 */
export function buildQuestionSequence(subs: SubForQuestions[]): GuidedQuestion[] {
  const list: GuidedQuestion[] = []

  for (const [i, sub] of subs.entries()) {
    const name = sub.displayName || 'Sub'
    list.push({
      id: `subcontract-${sub.projectVendorId}`,
      kind: 'subcontract_amount',
      phaseLabel: 'Subcontract Amount',
      title: i === 0
        ? 'What is your Subcontract Amount for this job?'
        : `What is the Subcontract Amount for ${name}?`,
      body: 'The full amount you agreed to pay this sub on this job — not just what has been invoiced.',
      projectVendorId: sub.projectVendorId,
      vendorName: name,
    })
  }

  list.push(
    {
      id: 'contract',
      kind: 'contract',
      phaseLabel: 'Your contract',
      title: 'Your contract with the owner',
      body: 'Original contract plus approved owner change orders. This is display-only — edit change orders from Accounting.',
    },
    {
      id: 'sub-cos',
      kind: 'sub_change_orders',
      phaseLabel: 'Sub change orders',
      title: 'Any sub change orders on this job?',
      body: 'Add as many as you have — you can add more later from this tab.',
      repeatable: true,
    },
    {
      id: 'labor',
      kind: 'labor',
      phaseLabel: 'Your labor',
      title: 'Your own labor on this job',
      body: 'What you or your crew have spent in labor on this job so far. Exact dollars only — nothing estimated.',
    },
    {
      id: 'overhead',
      kind: 'overhead',
      phaseLabel: 'Overhead',
      title: 'Overhead / general conditions',
      body: 'Supervision, temp facilities, insurance allocations — job-level overhead you want in the margin.',
    },
    {
      id: 'other-costs',
      kind: 'other_costs',
      phaseLabel: 'Other costs',
      title: 'Any other costs on this job?',
      body: 'Materials, equipment, permits, a card charge — anything you paid or owe that is not a subcontractor invoice. Add one at a time.',
      repeatable: true,
    },
  )

  return list
}

export function questionProgressLabel(
  index: number,
  total: number,
  phaseLabel: string,
  opts?: { kind?: QuestionKind },
): string {
  if (opts?.kind === 'other_costs') {
    return 'Other costs · add as many as you need'
  }
  if (opts?.kind === 'sub_change_orders') {
    return 'Sub change orders · add as many as you have'
  }
  return `Question ${index + 1} of ${total} · ${phaseLabel}`
}

/** Advance past skipped / answered; clamp to last question. */
export function nextQuestionIndex(
  current: number,
  total: number,
  skippedIds: Set<string> | string[],
  questions: GuidedQuestion[],
): number {
  const skipped = skippedIds instanceof Set ? skippedIds : new Set(skippedIds)
  let i = current + 1
  while (i < total && skipped.has(questions[i].id)) i += 1
  return Math.min(i, Math.max(0, total - 1))
}
